import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { 
  Search, Loader2, GraduationCap, MapPin, Clock, BookOpen, Award,
  Calendar, DollarSign, FileText, Upload, CheckCircle, ArrowRight,
  ArrowLeft, Building2, Globe, Star, Users, Phone, Mail, Facebook,
  Twitter, Instagram, Linkedin, ChevronRight, Filter, X, Eye, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Public Portal Home Page
export const PublicPortalPage = () => {
  const { workspace, slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [searchOptions, setSearchOptions] = useState(null);
  const [searchFilters, setSearchFilters] = useState({
    degree_type: '',
    country: '',
    subject: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, optionsRes] = await Promise.all([
          axios.get(`${API_BASE}/api/portal/${workspace}/${slug}`),
          axios.get(`${API_BASE}/api/portal/${workspace}/search-options`)
        ]);
        setPageData(pageRes.data);
        setSearchOptions(optionsRes.data);
      } catch (error) {
        console.error('Failed to load portal:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [workspace, slug]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchFilters.degree_type) params.set('degree_type', searchFilters.degree_type);
    if (searchFilters.country) params.set('country', searchFilters.country);
    if (searchFilters.subject) params.set('subject', searchFilters.subject);
    navigate(`/portal/${workspace}/search?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Portal Not Found</h1>
          <p className="text-gray-600 mt-2">This portal page does not exist or is not published.</p>
        </div>
      </div>
    );
  }

  const c = pageData.content || {};
  const colors = pageData.colors || { primary: '#3b82f6', secondary: '#1e40af' };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl">{c.companyName || 'Education Portal'}</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#search" className="text-gray-600 hover:text-gray-900">Find Programs</a>
            <a href={`/portal/${workspace}/universities`} className="text-gray-600 hover:text-gray-900">Universities</a>
            <a href={`/portal/${workspace}/about`} className="text-gray-600 hover:text-gray-900">About Us</a>
            <a href={`/portal/${workspace}/contact`} className="text-gray-600 hover:text-gray-900">Contact</a>
            <Button 
              style={{ backgroundColor: colors.primary }}
              onClick={() => navigate(`/portal/${workspace}/login`)}
            >
              Student Login
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Search */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)` }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-white"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full bg-white"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 py-16">
          <div className="text-center text-white mb-10">
            <p className="text-sm font-medium mb-4 opacity-90">{c.tagline || 'Your Gateway to World-Class Education'}</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight whitespace-pre-line">
              {c.heroTitle || 'Start Your Journey to\nGlobal Education'}
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              {c.heroSubtitle || 'Find and apply to programs from top universities worldwide.'}
            </p>
          </div>
          
          {/* Search Box */}
          <div id="search" className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">{c.searchTitle || 'Find Your Perfect Program'}</h2>
                <p className="text-gray-600 text-sm mt-1">{c.searchSubtitle || 'Search from our database of universities and programs'}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Degree Type Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Degree Type</label>
                  <select 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
                    value={searchFilters.degree_type}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, degree_type: e.target.value }))}
                  >
                    <option value="">Select a degree type</option>
                    {searchOptions?.degree_types?.map((dt, i) => (
                      <option key={i} value={dt.id}>{dt.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Location Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                  <select 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
                    value={searchFilters.country}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, country: e.target.value }))}
                  >
                    <option value="">Select a location</option>
                    {searchOptions?.countries?.map((country, i) => (
                      <option key={i} value={country.name}>{country.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Subject Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <select 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700"
                    value={searchFilters.subject}
                    onChange={(e) => setSearchFilters(prev => ({ ...prev, subject: e.target.value }))}
                  >
                    <option value="">Select a subject</option>
                    {searchOptions?.subjects?.map((subj, i) => (
                      <option key={i} value={subj.name}>{subj.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <Button 
                className="w-full py-6 text-lg"
                style={{ backgroundColor: colors.primary }}
                onClick={handleSearch}
              >
                <Search className="w-5 h-5 mr-2" />
                Find Programs
              </Button>
              
              <p className="text-center text-gray-500 text-sm mt-4">
                {searchOptions?.universities_count || 0}+ Partner Universities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {c.stats && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {c.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl font-bold" style={{ color: colors.primary }}>{stat.number}</div>
                  <div className="text-gray-600 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Countries Section */}
      {c.countries && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Study Destinations</h2>
              <p className="text-gray-600">Explore top education destinations worldwide.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {c.countries.map((country, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setSearchFilters(prev => ({ ...prev, country: country.name }));
                    handleSearch();
                  }}
                  className="p-4 rounded-xl border hover:shadow-lg transition-all cursor-pointer text-left"
                >
                  <div className="text-4xl mb-2">{country.flag}</div>
                  <h3 className="font-semibold text-sm">{country.name}</h3>
                  <p className="text-xs text-gray-500">{country.universities} Universities</p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-xl">{c.companyName}</span>
              </div>
              <p className="text-gray-400 mb-4">{c.tagline}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#search" className="hover:text-white">Find Programs</a></li>
                <li><a href={`/portal/${workspace}/universities`} className="hover:text-white">Universities</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-3 text-gray-400">
                {c.footerPhone && (
                  <li className="flex items-center gap-2">
                    <Phone className="w-4 h-4" /> {c.footerPhone}
                  </li>
                )}
                {c.footerEmail && (
                  <li className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> {c.footerEmail}
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © 2026 {c.companyName}. All rights reserved. Powered by EduBridge Network
          </div>
        </div>
      </footer>
    </div>
  );
};

// Search Results Page
export const PortalSearchPage = () => {
  const { workspace } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState({ programs: [], total: 0 });
  const [searchOptions, setSearchOptions] = useState(null);
  const [filters, setFilters] = useState({
    degree_type: searchParams.get('degree_type') || '',
    country: searchParams.get('country') || '',
    subject: searchParams.get('subject') || '',
    search: searchParams.get('q') || ''
  });
  const [page, setPage] = useState(1);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.degree_type) params.set('degree_type', filters.degree_type);
      if (filters.country) params.set('country', filters.country);
      if (filters.subject) params.set('subject', filters.subject);
      if (filters.search) params.set('search', filters.search);
      params.set('page', page.toString());
      params.set('limit', '20');

      const response = await axios.get(`${API_BASE}/api/portal/${workspace}/search?${params.toString()}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  }, [workspace, filters, page]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/portal/${workspace}/search-options`);
        setSearchOptions(res.data);
      } catch (error) {
        console.error('Failed to load search options:', error);
      }
    };
    fetchOptions();
  }, [workspace]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold">Program Search</span>
              </div>
            </div>
            <Button onClick={() => navigate(`/portal/${workspace}/login`)}>
              Student Login
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Search Input */}
              <div className="md:col-span-2">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    placeholder="Search programs..." 
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Degree Type */}
              <div>
                <Label className="text-xs">Degree Type</Label>
                <Select value={filters.degree_type || "all"} onValueChange={(v) => handleFilterChange('degree_type', v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All degrees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Degrees</SelectItem>
                    {searchOptions?.degree_types?.map((dt) => (
                      <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Country */}
              <div>
                <Label className="text-xs">Country</Label>
                <Select value={filters.country || "all"} onValueChange={(v) => handleFilterChange('country', v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {searchOptions?.countries?.map((c) => (
                      <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Subject */}
              <div>
                <Label className="text-xs">Subject</Label>
                <Select value={filters.subject || "all"} onValueChange={(v) => handleFilterChange('subject', v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All subjects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subjects</SelectItem>
                    {searchOptions?.subjects?.map((s) => (
                      <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Active Filters */}
            {(filters.degree_type || filters.country || filters.subject) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {filters.degree_type && (
                  <Badge variant="secondary" className="gap-1">
                    {searchOptions?.degree_types?.find(d => d.id === filters.degree_type)?.name}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('degree_type', '')} />
                  </Badge>
                )}
                {filters.country && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.country}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('country', '')} />
                  </Badge>
                )}
                {filters.subject && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.subject}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => handleFilterChange('subject', '')} />
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">
            {loading ? 'Searching...' : `Found ${results.total} programs`}
          </p>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : results.programs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold">No programs found</h3>
              <p className="text-gray-600 mt-2">Try adjusting your search filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {results.programs.map((program) => (
              <Card key={program.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/portal/${workspace}/program/${program.id}`)}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-blue-600" />
                    </div>
                    {program.degree_type && (
                      <Badge variant="secondary">{program.degree_type.name}</Badge>
                    )}
                  </div>
                  <CardTitle className="text-lg mt-3">{program.name}</CardTitle>
                  <CardDescription>
                    {program.university?.name || 'University'}
                    {program.campus?.city && ` • ${program.campus.city}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-gray-600">
                    {program.university?.country && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {program.university.country}
                      </div>
                    )}
                    {program.duration && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {program.duration}
                      </div>
                    )}
                    {program.subject && (
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        {program.subject}
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter>
                  <Button className="w-full" variant="outline">
                    View Details <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {results.total_pages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button 
              variant="outline" 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-4">
              Page {page} of {results.total_pages}
            </span>
            <Button 
              variant="outline" 
              onClick={() => setPage(p => Math.min(results.total_pages, p + 1))}
              disabled={page === results.total_pages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Program Detail Page
export const PortalProgramDetailPage = () => {
  const { workspace, programId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [studentToken, setStudentToken] = useState(localStorage.getItem(`portal_token_${workspace}`));
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    country: ''
  });
  const [applyData, setApplyData] = useState({
    intake: '',
    notes: ''
  });
  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/portal/${workspace}/program/${programId}`);
        setProgramData(res.data);
      } catch (error) {
        console.error('Failed to load program:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgram();
  }, [workspace, programId]);

  const handleAuth = async () => {
    setSubmitting(true);
    try {
      if (authMode === 'signup') {
        const res = await axios.post(`${API_BASE}/api/portal/${workspace}/signup`, formData);
        toast.success('Account created! Please login.');
        setAuthMode('login');
      } else {
        const res = await axios.post(`${API_BASE}/api/portal/${workspace}/login`, {
          email: formData.email,
          password: formData.password
        });
        localStorage.setItem(`portal_token_${workspace}`, res.data.access_token);
        setStudentToken(res.data.access_token);
        toast.success('Logged in successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApply = async () => {
    if (!studentToken) {
      setShowApply(true);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/api/portal/${workspace}/student/apply`,
        {
          program_id: programId,
          university_id: programData?.university?.id,
          intake: applyData.intake,
          notes: applyData.notes
        },
        { headers: { Authorization: `Bearer ${studentToken}` } }
      );
      toast.success('Application submitted successfully!');
      setShowApply(false);
      navigate(`/portal/${workspace}/my-applications`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!programData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Program Not Found</h1>
        </div>
      </div>
    );
  }

  const { program, university, campus, degree_type, required_documents, intakes } = programData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Search
              </Button>
            </div>
            <Button onClick={() => setShowApply(true)}>
              Apply Now
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Program Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-blue-100 flex items-center justify-center">
                    <GraduationCap className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl font-bold text-gray-900">{program.name}</h1>
                    <p className="text-gray-600 mt-1">{university?.name || 'University'}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {degree_type && <Badge>{degree_type.name}</Badge>}
                      {university?.country && <Badge variant="outline">{university.country}</Badge>}
                      {campus?.city && <Badge variant="outline">{campus.city}</Badge>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Program Details */}
            <Card>
              <CardHeader>
                <CardTitle>Program Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {program.duration && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Duration</p>
                        <p className="font-medium">{program.duration}</p>
                      </div>
                    </div>
                  )}
                  {program.subject && (
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Subject</p>
                        <p className="font-medium">{program.subject}</p>
                      </div>
                    </div>
                  )}
                  {program.tuition_fee && (
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Tuition Fee</p>
                        <p className="font-medium">{program.tuition_fee}</p>
                      </div>
                    </div>
                  )}
                  {program.faculty && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">Faculty</p>
                        <p className="font-medium">{program.faculty}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Required Documents */}
            {required_documents && required_documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Required Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {required_documents.map((doc, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span>{doc.name}</span>
                        {doc.required && <Badge variant="destructive" className="ml-auto">Required</Badge>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card */}
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle>Ready to Apply?</CardTitle>
                <CardDescription>Start your application for this program</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Intake Selection */}
                <div>
                  <Label>Select Intake</Label>
                  <Select value={applyData.intake} onValueChange={(v) => setApplyData(prev => ({ ...prev, intake: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose intake" />
                    </SelectTrigger>
                    <SelectContent>
                      {(intakes || ['September 2025', 'January 2026', 'May 2026']).map((intake, i) => (
                        <SelectItem key={i} value={intake}>{intake}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button className="w-full" size="lg" onClick={() => setShowApply(true)}>
                  Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                
                <p className="text-xs text-gray-500 text-center">
                  Create an account or login to submit your application
                </p>
              </CardContent>
            </Card>

            {/* University Info */}
            {university && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">About {university.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    {university.country && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {university.country}
                      </div>
                    )}
                    {university.website && (
                      <a 
                        href={university.website} 
                        target="_blank" 
                        rel="noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Globe className="w-4 h-4" />
                        Visit Website
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      <Dialog open={showApply} onOpenChange={setShowApply}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {studentToken ? 'Submit Application' : (authMode === 'signup' ? 'Create Account' : 'Login')}
            </DialogTitle>
            <DialogDescription>
              {studentToken 
                ? `Apply for ${program.name}` 
                : (authMode === 'signup' ? 'Create an account to submit your application' : 'Login to continue')
              }
            </DialogDescription>
          </DialogHeader>
          
          {!studentToken ? (
            <div className="space-y-4">
              <Tabs value={authMode} onValueChange={setAuthMode}>
                <TabsList className="w-full">
                  <TabsTrigger value="signup" className="flex-1">Sign Up</TabsTrigger>
                  <TabsTrigger value="login" className="flex-1">Login</TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="space-y-3">
                {authMode === 'signup' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>First Name</Label>
                        <Input 
                          value={formData.first_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Last Name</Label>
                        <Input 
                          value={formData.last_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input 
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input 
                        value={formData.country}
                        onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input 
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  />
                </div>
              </div>
              
              <Button className="w-full" onClick={handleAuth} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {authMode === 'signup' ? 'Create Account' : 'Login'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Selected Program</Label>
                <p className="text-sm text-gray-600 mt-1">{program.name}</p>
              </div>
              
              <div>
                <Label>Intake</Label>
                <Select value={applyData.intake} onValueChange={(v) => setApplyData(prev => ({ ...prev, intake: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select intake" />
                  </SelectTrigger>
                  <SelectContent>
                    {(intakes || ['September 2025', 'January 2026', 'May 2026']).map((intake, i) => (
                      <SelectItem key={i} value={intake}>{intake}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Additional Notes (Optional)</Label>
                <textarea 
                  className="w-full p-2 border rounded-md text-sm mt-1"
                  rows={3}
                  value={applyData.notes}
                  onChange={(e) => setApplyData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information..."
                />
              </div>
              
              <Button className="w-full" onClick={handleApply} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit Application
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Universities Listing Page
export const PortalUniversitiesPage = () => {
  const { workspace } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [universities, setUniversities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [portalInfo, setPortalInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [uniRes, infoRes] = await Promise.all([
          axios.get(`${API_BASE}/api/portal/${workspace}/universities`),
          axios.get(`${API_BASE}/api/portal/${workspace}/search-options`)
        ]);
        setUniversities(uniRes.data);
        setPortalInfo(infoRes.data);
      } catch (error) {
        console.error('Failed to load universities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [workspace]);

  const filteredUniversities = universities.filter(uni => 
    uni.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/portal/${workspace}/home`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl">Universities</span>
            </div>
          </div>
          <Button onClick={() => navigate(`/portal/${workspace}/login`)}>
            Student Login
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Our Partner Universities</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Explore our network of {universities.length}+ prestigious universities across the globe
          </p>
        </div>
      </section>

      {/* Search and List */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search universities..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* University Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUniversities.map((uni, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/portal/${workspace}/search?university=${uni.name}`)}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                    {uni.logo_url ? (
                      <img src={uni.logo_url} alt={uni.name} className="w-12 h-12 object-contain" />
                    ) : (
                      <Building2 className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg truncate">{uni.name}</h3>
                    <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{uni.country}</span>
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 text-sm mt-2">
                      <BookOpen className="w-4 h-4" />
                      <span>{uni.program_count || 0} Programs</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredUniversities.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No universities found matching your search.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400">
          <p>© 2026 Education Portal. Powered by EduBridge Network</p>
        </div>
      </footer>
    </div>
  );
};

// About Us Page
export const PortalAboutPage = () => {
  const { workspace } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try to get the about page content, fallback to home page content
        const res = await axios.get(`${API_BASE}/api/portal/${workspace}/home`);
        setPageData(res.data);
      } catch (error) {
        console.error('Failed to load about page:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [workspace]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const c = pageData?.content || {};
  const colors = pageData?.colors || { primary: '#3b82f6', secondary: '#1e40af' };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/portal/${workspace}/home`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl">{c.companyName || 'Education Portal'}</span>
            </div>
          </div>
          <Button 
            style={{ backgroundColor: colors.primary }}
            onClick={() => navigate(`/portal/${workspace}/login`)}
          >
            Student Login
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-20" style={{ background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)` }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-white"></div>
          <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full bg-white"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Us</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            {c.tagline || 'Your trusted partner in achieving your global education dreams'}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
              <p className="text-gray-600 text-lg mb-6">
                We are dedicated to helping students achieve their dreams of studying abroad. 
                With years of experience and partnerships with top universities worldwide, 
                we provide comprehensive guidance from university selection to visa approval.
              </p>
              <p className="text-gray-600 text-lg">
                Our team of experienced counselors understands the unique aspirations of each student 
                and works tirelessly to match them with the perfect academic program and institution.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {(c.stats || [
                { number: '500+', label: 'Partner Universities' },
                { number: '15,000+', label: 'Students Placed' },
                { number: '50+', label: 'Countries' },
                { number: '98%', label: 'Visa Success' },
              ]).map((stat, i) => (
                <Card key={i} className="text-center p-6">
                  <div className="text-3xl font-bold" style={{ color: colors.primary }}>{stat.number}</div>
                  <div className="text-gray-600 text-sm mt-1">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We offer end-to-end support for your international education journey
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {(c.features || [
              { icon: 'GraduationCap', title: 'University Selection', description: 'Expert guidance to find the perfect university matching your profile.' },
              { icon: 'BookOpen', title: 'Application Support', description: 'Complete assistance with essays, documents, and submissions.' },
              { icon: 'Award', title: 'Scholarship Guidance', description: 'Find and apply for scholarships to fund your education.' },
              { icon: 'CheckCircle', title: 'Visa Assistance', description: '98% visa success rate with our expert counselors.' },
            ]).map((feature, i) => {
              const IconComponent = { GraduationCap, BookOpen, Award, CheckCircle }[feature.icon] || GraduationCap;
              return (
                <Card key={i} className="p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.primary + '20' }}>
                    <IconComponent className="w-6 h-6" style={{ color: colors.primary }} />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">{c.ctaTitle || 'Ready to Begin Your Journey?'}</h2>
          <p className="text-gray-600 mb-8">{c.ctaSubtitle || 'Book a free consultation with our expert counselors today.'}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              style={{ backgroundColor: colors.primary }}
              onClick={() => navigate(`/portal/${workspace}/search`)}
            >
              <Search className="w-5 h-5 mr-2" />
              Explore Programs
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate(`/portal/${workspace}/contact`)}
            >
              <Phone className="w-5 h-5 mr-2" />
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400">
          <p>© 2026 {c.companyName || 'Education Portal'}. Powered by EduBridge Network</p>
        </div>
      </footer>
    </div>
  );
};

// Contact Page
export const PortalContactPage = () => {
  const { workspace } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    message: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/portal/${workspace}/home`);
        setPageData(res.data);
      } catch (error) {
        console.error('Failed to load contact page:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [workspace]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/portal/${workspace}/enquiry`, formData);
      setSubmitted(true);
      toast.success('Your message has been sent!');
    } catch (error) {
      console.error('Failed to submit enquiry:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const c = pageData?.content || {};
  const colors = pageData?.colors || { primary: '#3b82f6', secondary: '#1e40af' };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/portal/${workspace}/home`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colors.primary }}>
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl">{c.companyName || 'Education Portal'}</span>
            </div>
          </div>
          <Button 
            style={{ backgroundColor: colors.primary }}
            onClick={() => navigate(`/portal/${workspace}/login`)}
          >
            Student Login
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden py-16" style={{ background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)` }}>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="p-8">
                <CardHeader className="p-0 mb-6">
                  <CardTitle>Send us a Message</CardTitle>
                  <CardDescription>Fill out the form below and we'll get back to you shortly.</CardDescription>
                </CardHeader>
                
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.primary + '20' }}>
                      <CheckCircle className="w-8 h-8" style={{ color: colors.primary }} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                    <p className="text-gray-600">Thank you for reaching out. We'll get back to you soon.</p>
                    <Button 
                      className="mt-6"
                      variant="outline"
                      onClick={() => {
                        setSubmitted(false);
                        setFormData({ name: '', email: '', phone: '', country: '', message: '' });
                      }}
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+1 234 567 8900"
                        />
                      </div>
                      <div>
                        <Label>Country</Label>
                        <Input
                          value={formData.country}
                          onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                          placeholder="Your country"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Message *</Label>
                      <textarea
                        className="w-full p-3 border rounded-md text-sm mt-1 resize-none"
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="How can we help you?"
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full"
                      style={{ backgroundColor: colors.primary }}
                      disabled={submitting}
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Send Message
                    </Button>
                  </form>
                )}
              </Card>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6">Get in Touch</h2>
                <div className="space-y-4">
                  {c.footerPhone && (
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: colors.primary + '20' }}>
                        <Phone className="w-5 h-5" style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Phone</h3>
                        <p className="text-gray-600">{c.footerPhone}</p>
                      </div>
                    </div>
                  )}
                  {c.footerEmail && (
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: colors.primary + '20' }}>
                        <Mail className="w-5 h-5" style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Email</h3>
                        <p className="text-gray-600">{c.footerEmail}</p>
                      </div>
                    </div>
                  )}
                  {c.footerAddress && (
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: colors.primary + '20' }}>
                        <MapPin className="w-5 h-5" style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <h3 className="font-semibold">Address</h3>
                        <p className="text-gray-600">{c.footerAddress}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Office Hours */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Office Hours</h3>
                <div className="space-y-2 text-gray-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </Card>

              {/* Social Links */}
              <div>
                <h3 className="font-semibold mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a href="#" className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <Facebook className="w-5 h-5 text-gray-600" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <Twitter className="w-5 h-5 text-gray-600" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5 text-gray-600" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <Linkedin className="w-5 h-5 text-gray-600" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400">
          <p>© 2026 {c.companyName || 'Education Portal'}. Powered by EduBridge Network</p>
        </div>
      </footer>
    </div>
  );
};

// Student Applications Dashboard Page
export const PortalStudentApplicationsPage = () => {
  const { workspace } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [student, setStudent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem(`portal_token_${workspace}`);
      if (!token) {
        navigate(`/portal/${workspace}/search`);
        toast.error('Please login to view your applications');
        return;
      }

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [appsRes, profileRes] = await Promise.all([
          axios.get(`${API_BASE}/api/portal/${workspace}/student/applications`, { headers }),
          axios.get(`${API_BASE}/api/portal/${workspace}/student/profile`, { headers })
        ]);
        setApplications(appsRes.data.applications);
        setStudent(profileRes.data);
      } catch (err) {
        console.error('Failed to load applications:', err);
        if (err.response?.status === 401) {
          localStorage.removeItem(`portal_token_${workspace}`);
          navigate(`/portal/${workspace}/search`);
          toast.error('Session expired. Please login again.');
        } else {
          setError('Failed to load your applications');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [workspace, navigate]);

  const handleLogout = () => {
    localStorage.removeItem(`portal_token_${workspace}`);
    navigate(`/portal/${workspace}/search`);
    toast.success('Logged out successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'active': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-amber-100 text-slate-800',
      'approved': 'bg-emerald-100 text-emerald-800',
      'rejected': 'bg-red-100 text-red-800',
      'completed': 'bg-gray-100 text-gray-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">{error}</h1>
          <Button className="mt-4" onClick={() => navigate(`/portal/${workspace}/search`)}>
            Go to Search
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl">My Applications</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/portal/${workspace}/search`)}>
              <Search className="w-4 h-4 mr-2" />
              Find Programs
            </Button>
            <Button variant="ghost" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Student Info Banner */}
      {student && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Users className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  Welcome, {student.first_name} {student.last_name}
                </h1>
                <p className="opacity-90">{student.email}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Applications List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Your Applications ({applications.length})</h2>
          <Button onClick={() => navigate(`/portal/${workspace}/search`)}>
            <Plus className="w-4 h-4 mr-2" />
            Apply to New Program
          </Button>
        </div>

        {applications.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Applications Yet</h3>
            <p className="text-gray-600 mb-6">
              You haven't submitted any applications yet. Start by searching for programs that interest you.
            </p>
            <Button onClick={() => navigate(`/portal/${workspace}/search`)}>
              <Search className="w-4 h-4 mr-2" />
              Find Programs
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">
                          {app.program_name || app.program_id || 'Program'}
                        </h3>
                        <Badge className={getStatusColor(app.status)}>
                          {app.status?.charAt(0).toUpperCase() + app.status?.slice(1) || 'New'}
                        </Badge>
                      </div>
                      {app.university_name && (
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                          <Building2 className="w-4 h-4" />
                          <span>{app.university_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {app.intake && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>Intake: {app.intake}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>Applied: {new Date(app.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {app.notes && (
                        <p className="text-gray-600 text-sm mt-3 border-t pt-3">{app.notes}</p>
                      )}
                    </div>
                    <div className="ml-4">
                      <Button variant="outline" size="sm" onClick={() => navigate(`/portal/${workspace}/program/${app.program_id}`)}>
                        <Eye className="w-4 h-4 mr-1" />
                        View Program
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400">
          <p>© 2026 Education Portal. Powered by EduBridge Network</p>
        </div>
      </footer>
    </div>
  );
};

// Student Login Page
export const PortalLoginPage = () => {
  const { workspace } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    country: ''
  });

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem(`portal_token_${workspace}`);
    if (token) {
      navigate(`/portal/${workspace}/my-applications`);
    }
  }, [workspace, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (authMode === 'signup') {
        await axios.post(`${API_BASE}/api/portal/${workspace}/signup`, formData);
        toast.success('Account created! Please login.');
        setAuthMode('login');
      } else {
        const res = await axios.post(`${API_BASE}/api/portal/${workspace}/login`, {
          email: formData.email,
          password: formData.password
        });
        localStorage.setItem(`portal_token_${workspace}`, res.data.access_token);
        toast.success('Logged in successfully!');
        navigate(`/portal/${workspace}/my-applications`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/portal/${workspace}/search`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Search
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl">Student Portal</span>
          </div>
          <div className="w-32" /> {/* Spacer for centering */}
        </div>
      </nav>

      {/* Login Form */}
      <div className="flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>{authMode === 'login' ? 'Welcome Back' : 'Create Account'}</CardTitle>
            <CardDescription>
              {authMode === 'login' 
                ? 'Login to view your applications' 
                : 'Sign up to apply for programs'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={authMode} onValueChange={setAuthMode} className="mb-6">
              <TabsList className="w-full">
                <TabsTrigger value="login" className="flex-1">Login</TabsTrigger>
                <TabsTrigger value="signup" className="flex-1">Sign Up</TabsTrigger>
              </TabsList>
            </Tabs>

            <form onSubmit={handleSubmit} className="space-y-4">
              {authMode === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
              
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {authMode === 'login' ? 'Login' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400">
          <p>© 2026 Education Portal. Powered by EduBridge Network</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicPortalPage;
